import { useMemo } from 'react';
import { SearchSuggestion } from '../../components/SearchForm/types';
import useProviderNames from '../../api/useProviderNames';
import { useOrgIds } from '../../hooks/useSearchOrgIds';
import { getDOBFormat } from '../../utils/date';
import useGetLeadsListQuery from './api/useGetLeadsListQuery';

// Feeds lead/form-fill quick-results into the header search dropdown. [HWM-23014] Cross-provider:
// searches every org the login can reach (repeated ?hero_organization_id=) and attributes each
// result to its provider, mirroring useMemberSearchSuggestions.
const useLeadSearchSuggestions = (query: string) => {
    const orgIds = useOrgIds();
    const isMultiOrg = orgIds.length > 1;
    const providerNames = useProviderNames(orgIds);

    const { data, isFetching } = useGetLeadsListQuery(
        { search: query, limit: 5, offset: 0 },
        { enabled: !!query, heroOrgIds: orgIds }
    );

    // Mapped inline (not via useSuggestionList) so the provider-name label recomputes when
    // provider names resolve asynchronously — useSuggestionList memoizes on rows only.
    const suggestions = useMemo<SearchSuggestion[]>(
        () =>
            (data?.leads ?? []).map((lead) => {
                const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim();
                const dob = getDOBFormat(lead.date_of_birth, 'MM/dd/yyyy');
                const subtitle = [lead.email, dob && `DOB: ${dob}`].filter(Boolean).join(' | ');
                const providerName = lead.hero_organization_id
                    ? providerNames[lead.hero_organization_id]
                    : undefined;

                return {
                    id: lead.lead_id,
                    title: name || lead.email || 'Unknown lead',
                    subtitle: subtitle || undefined,
                    // Show provider attribution only for multi-org logins; single-tenant keeps state.
                    aside: (isMultiOrg ? providerName : undefined) ?? lead.state ?? undefined,
                    // Selecting a result from another provider switches tenant first, otherwise the
                    // detail page loads under the wrong org and 403s.
                    heroOrgId: lead.hero_organization_id ?? undefined,
                    to: `/form-fills/${lead.lead_id}`
                };
            }),
        [data, providerNames, isMultiOrg]
    );

    return { suggestions, isFetching };
};

export default useLeadSearchSuggestions;
